
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useDeviceFingerprint } from '@/hooks/useDeviceFingerprint';
import { loadReferentFormData } from '@/services/referentFormDataLoader';
import { submitReferentResponse } from '@/services/referentFormSubmission';
import { formPersistence } from '@/services/formPersistence';
import type { SelectedCriterion } from '@/types/evaluationCriteria';
import type { ReferentFormData } from '@/types/referentForm';
import { logger } from '@/utils/productionLogger';

export const useReferentForm = () => {
  const params = useParams();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ReferentFormData>({
    relationTypeConfirmed: '',
    collaborationStartDate: '',
    collaborationEndDate: '',
    ratings: {},
  });
  const [campaign, setCampaign] = useState<any>(null);
  const [referent, setReferent] = useState<any>(null);
  const [evaluationCriteria, setEvaluationCriteria] = useState<SelectedCriterion[]>([]);
  const [fingerprintConsent, setFingerprintConsent] = useState(false);

  // Device fingerprinting avec consentement
  const { fingerprint, isGenerating, error: fingerprintError } = useDeviceFingerprint(fingerprintConsent);

  // Récupérer le token directement depuis l'URL path
  const token = params.token;

  logger.debug('Token récupéré depuis l\'URL', { token });

  const loadReferentData = async () => {
    try {
      if (!token) {
        throw new Error('Token manquant dans l\'URL');
      }

      // PHASE 2: Plus de validation de contexte - la sécurité est assurée par les RLS policies
      // et les tokens dans les RPC. On charge directement les données.
      
      logger.debug('Début chargement données référent');
      
      const { referent: loadedReferent, campaign: loadedCampaign, evaluationCriteria: criteria, initialRatings } = 
        await loadReferentFormData(token);

      setReferent(loadedReferent);
      setCampaign(loadedCampaign);
      setEvaluationCriteria(criteria);
      setFormData(prev => ({ ...prev, ratings: initialRatings }));

      logger.debug('Données référent chargées avec succès');

    } catch (error: any) {
      console.error('💥 Erreur lors du chargement des données:', error);
      
      // Gestion spécifique des erreurs
      let errorMessage = error.message;
      
      if (error.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
          error.name === 'ERR_BLOCKED_BY_CLIENT') {
        errorMessage = "Votre navigateur ou une extension bloque la requête. Essayez de désactiver les bloqueurs de publicité ou les extensions de confidentialité.";
      } else if (error.message?.includes('introuvable')) {
        errorMessage = "Ce lien de référence n'est pas valide ou a expiré.";
      } else if (error.message?.includes('Contexte d\'accès')) {
        errorMessage = "Lien de référence invalide ou expiré.";
      } else if (!error.message) {
        errorMessage = "Impossible de charger les données du formulaire";
      }
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw error;
    }
  };

  const updateFormData = (data: Partial<ReferentFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  // Fonction de validation des étapes
  const validateCurrentStep = (step: number, data: any): boolean => {
    switch (step) {
      case 1: // Informations contextuelles
        return !!(data.relationTypeConfirmed);
      
      case 2: // Signature
        return !!(data.signedDocumentUrl);
      
      case 3: // Période de collaboration
        return !!(data.collaborationStartDate);
      
      case 4: // Évaluation candidat
        return data.ratings && Object.keys(data.ratings).length > 0;
      
      case 5: // Questions critiques
        return data.criticalQuestions && 
               data.criticalQuestions.wouldRecommend !== null &&
               data.criticalQuestions.wouldRehire !== null &&
               data.criticalQuestions.hasIncidents !== null &&
               // Si hasIncidents est true, la description est obligatoire
               (data.criticalQuestions.hasIncidents === false || 
                (data.criticalQuestions.hasIncidents === true && 
                 data.criticalQuestions.incidentsDescription?.trim()));
      
      case 6: // Enregistrement audio (obligatoire)
        return !!(data.audioFileUrl || data.audioTranscription);
      
      case 7: // Confirmation
        return true; // Dernière étape, toujours valide
      
      default:
        return false;
    }
  };

  // Navigation entre les étapes
  const handleNext = useCallback(() => {
    if (currentStep < 7 && validateCurrentStep(currentStep, formData)) {
      setCurrentStep(prev => prev + 1);
      // Scroll vers le haut lors du changement d'étape
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, formData]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      // Scroll vers le haut lors du changement d'étape
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const isLastStep = currentStep === 7;
  const canGoNext = currentStep < 7 && validateCurrentStep(currentStep, formData);
  const canGoPrevious = currentStep > 1;
  
  // Validation pour la soumission : toutes les étapes 1-6 doivent être valides
  const canSubmit = useMemo(() => {
    if (currentStep !== 7) return false;
    
    // Vérifier que toutes les étapes précédentes sont valides
    for (let step = 1; step <= 6; step++) {
      if (!validateCurrentStep(step, formData)) {
        return false;
      }
    }
    return true;
  }, [currentStep, formData]);

  const submitResponse = async () => {
    setIsSubmitting(true);

    try {
      logger.debug('Début soumission avec données', {
        referentId: referent?.id,
        campaignId: campaign?.id,
        formData
      });

      // Validation des données avant soumission
      if (!referent || !campaign) {
        throw new Error('Données du référent ou de la campagne manquantes');
      }

      // Validation étape 1 - Informations contextuelles
      if (!formData.relationTypeConfirmed) {
        throw new Error('Type de relation manquant');
      }

      // Validation étape 2 - Signature
      if (!formData.signedDocumentUrl) {
        throw new Error('Signature manquante');
      }

      // Validation étape 3 - Période de collaboration
      if (!formData.collaborationStartDate) {
        throw new Error('Date de début de collaboration manquante');
      }

      // Validation étape 4 - Évaluations
      if (!formData.ratings || Object.keys(formData.ratings).length === 0) {
        throw new Error('Aucune évaluation fournie');
      }

      // Validation étape 5 - Questions critiques
      if (!formData.criticalQuestions || 
          formData.criticalQuestions.wouldRecommend === null ||
          formData.criticalQuestions.wouldRehire === null ||
          formData.criticalQuestions.hasIncidents === null) {
        throw new Error('Questions critiques incomplètes');
      }

      // Validation étape 6 - Enregistrement audio (obligatoire)
      if (!formData.audioFileUrl && !formData.audioTranscription) {
        throw new Error('Enregistrement audio manquant');
      }

      await submitReferentResponse(referent, campaign, formData, {
        fingerprintConsent,
        deviceFingerprint: fingerprintConsent ? fingerprint : null
      });

      // Nettoyer les données sauvegardées après soumission réussie
      if (referent?.id) {
        formPersistence.clearFormData(referent.id);
        logger.info('[useReferentForm] Données sauvegardées nettoyées après soumission', {
          referentId: referent.id
        });
      }

      toast({
        title: "Réponse soumise",
        description: "Merci pour votre évaluation !",
      });

      return true;
    } catch (error: any) {
      console.error('💥 Erreur lors de la soumission:', error);
      
      let errorMessage = 'Impossible de soumettre votre réponse';
      
      if (error.message?.includes('Données manquantes')) {
        errorMessage = 'Erreur de données : ' + error.message;
      } else if (error.message?.includes('Référent introuvable')) {
        errorMessage = 'Ce lien de référence n\'est plus valide';
      } else if (error.message?.includes('incomplètes')) {
        errorMessage = 'Veuillez remplir tous les champs obligatoires';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Effet pour charger les données sauvegardées au démarrage
  useEffect(() => {
    if (referent?.id) {
      const savedData = formPersistence.loadFormData(referent.id);
      if (savedData) {
        logger.info('[useReferentForm] Données sauvegardées trouvées', {
          referentId: referent.id,
          fieldsCount: Object.keys(savedData).length
        });
        
        setFormData(prev => ({ ...prev, ...savedData }));
        
        toast({
          title: "Données récupérées",
          description: "Vos données précédentes ont été restaurées.",
        });
      }
    }
  }, [referent?.id, toast]);

  // Effet pour démarrer la sauvegarde automatique
  useEffect(() => {
    if (referent?.id) {
      formPersistence.startAutoSave(referent.id, () => formData);
      
      return () => {
        formPersistence.stopAutoSave(referent.id);
      };
    }
  }, [referent?.id, formData]);

  // Effet pour sauvegarder à chaque changement de données
  useEffect(() => {
    if (referent?.id && Object.keys(formData).length > 0) {
      formPersistence.saveFormData(referent.id, formData);
    }
  }, [referent?.id, formData]);

  return {
    currentStep,
    setCurrentStep,
    formData,
    updateFormData,
    campaign,
    referent,
    evaluationCriteria,
    isSubmitting,
    loadReferentData,
    submitResponse,
    // Navigation
    handleNext,
    handlePrevious,
    isLastStep,
    canGoNext,
    canGoPrevious,
    canSubmit,
    // Fingerprinting
    fingerprintConsent,
    setFingerprintConsent,
    fingerprint,
    isGenerating: isGenerating,
    fingerprintError,
  };
};
